import apiService from './api';

export interface DashboardStats {
  totalTenants: number;
  totalSites: number;
  activeUsers: number;
  blockedSites: number;
  totalWeighings: number;
  pendingWeighings: number;
}

export interface TenantStats {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  sites_count: number;
}

export interface SiteStats {
  id: number;
  name: string;
  code: string;
  site_type: string;
  is_active: boolean;
  is_blocked: boolean;
  tenant: {
    id: number;
    name: string;
    code: string;
  };
}

export interface UserStats {
  id: number;
  username: string;
  full_name: string;
  email: string;
  is_active: boolean;
  role?: {
    id: number;
    name: string;
  };
}

export interface WeighingStats {
  total_pesées: number;
  pesées_en_cours: number;
  pesées_en_attente: number;
  pesées_complétées: number;
  pesées_refoulées: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

class DashboardService {
  /**
   * Récupérer toutes les statistiques du dashboard
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      
      const [tenantsResponse, sitesResponse, usersResponse, weighingsResponse] = await Promise.allSettled([
        this.getTenants(),
        this.getSites(),
        this.getUsers(),
        this.getWeighingStats()
      ]);

      const tenants = tenantsResponse.status === 'fulfilled' ? tenantsResponse.value : [];
      const sites = sitesResponse.status === 'fulfilled' ? sitesResponse.value : [];
      const users = usersResponse.status === 'fulfilled' ? usersResponse.value : [];
      const weighings = weighingsResponse.status === 'fulfilled' ? weighingsResponse.value : { total_pesées: 0, pesées_en_cours: 0, pesées_en_attente: 0, pesées_complétées: 0, pesées_refoulées: 0 };

      const stats = {
        totalTenants: tenants.length,
        totalSites: sites.length,
        activeUsers: users.filter(user => user.is_active).length,
        blockedSites: sites.filter(site => site.is_blocked).length,
        totalWeighings: weighings.total_pesées || 0,
        pendingWeighings: weighings.pesées_en_attente || 0
      };

      return stats;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des statistiques du dashboard:', error);
      throw error;
    }
  }

  /**
   * Récupérer la liste des tenants
   */
  async getTenants(): Promise<TenantStats[]> {
    try {
      const response = await apiService.get('/sites/tenants/');
      // Gestion robuste de la réponse
      let tenantsArray = [];
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // Nouvelle structure: { success: true, message: "...", data: [...] }
        tenantsArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Structure directe
        tenantsArray = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Structure avec results
        tenantsArray = response.data.results;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Structure avec data
        tenantsArray = response.data.data;
      } else {
        tenantsArray = [];
      }
      return tenantsArray;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des tenants:', error);
      return [];
    }
  }

  /**
   * Récupérer la liste des sites
   */
  async getSites(): Promise<SiteStats[]> {
    try {
      
      const response = await apiService.get('/sites/');
      // Gestion robuste de la réponse
      let sitesArray = [];
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // Nouvelle structure: { success: true, message: "...", data: [...] }
        sitesArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Structure directe
        sitesArray = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Structure avec results
        sitesArray = response.data.results;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Structure avec data
        sitesArray = response.data.data;
      } else {
        sitesArray = [];
      }
      return sitesArray;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des sites:', error);
      return [];
    }
  }

  /**
   * Récupérer la liste des utilisateurs
   */
  async getUsers(): Promise<UserStats[]> {
    try {
      const response = await apiService.get('/users/');
      // Gestion robuste de la réponse
      let usersArray = [];
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // Nouvelle structure: { success: true, message: "...", data: [...] }
        usersArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Structure directe
        usersArray = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Structure avec results
        usersArray = response.data.results;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Structure avec data
        usersArray = response.data.data;
      } else {
        usersArray = [];
      }
      return usersArray;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des utilisateurs:', error);
      return [];
    }
  }

  /**
   * Récupérer les statistiques des pesées
   */
  async getWeighingStats(): Promise<WeighingStats> {
    try {
      
      const response = await apiService.get('/weighing/api/statistics/');
      
      // Gestion de la nouvelle structure de réponse
      let stats: WeighingStats;
      if (response.data && response.data.success && response.data.data) {
        // Nouvelle structure: { success: true, message: "...", data: {...} }
        stats = response.data.data;
      } else if (response.data) {
        // Structure directe (fallback)
        stats = response.data;
      } else {
        // Valeurs par défaut
        stats = {
          total_pesées: 0,
          pesées_en_cours: 0,
          pesées_en_attente: 0,
          pesées_complétées: 0,
          pesées_refoulées: 0
        };
      }
      
      return stats;
    } catch (error) {
      return {
        total_pesées: 0,
        pesées_en_cours: 0,
        pesées_en_attente: 0,
        pesées_complétées: 0,
        pesées_refoulées: 0
      }
    }
  }

  /**
   * Récupérer les statistiques d'un site spécifique
   */
  async getSiteStatistics(siteCode: string): Promise<any> {
    try {
      const response = await apiService.get(`/sites/statistics/`, {
        headers: {
          'X-Active-Site': siteCode
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques du site:', error);
      throw error;
    }
  }

  /**
   * Récupérer les statistiques des notifications
   */
  async getNotificationStats(): Promise<any> {
    try {
      const response = await apiService.get('/notifications/stats/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des notifications:', error);
      return {};
    }
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
